import { Link } from "react-router-dom";
import { ScrollWithShadows } from "components/layout/utils/ScrollWithShadows";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { LiveIndicator } from "components/layout/organisation/LiveIndicator";
import { T } from "components/util/t";
import { Section } from "./HomePage";
import { useQuery } from "@apollo/client";
import chunk from "lodash/chunk";
import { FeaturedEventsSkeleton } from "./FeaturedEventsSkeleton";
import { EVENT_CARD_WIDTH, EventCard } from "components/routes/root/eventCard/EventCard";
import { GET_FEATURED_EVENTS } from "./FeaturedEvents.gql";
import { GetFeaturedEventsQuery } from "components/routes/root/FeaturedEvents.gql.types";
import { eventCardDate } from "models/Event";
import { EventCardTopLabel } from "components/routes/root/eventCard/EventCardTopLabel";
import { EventCardMainSection } from "components/routes/root/eventCard/EventCardMainSection";
import { EventCardOrganisation } from "components/routes/root/eventCard/EventCardOrganisation";
import { EventCardTitle } from "components/routes/root/eventCard/EventCardTitle";
import { EventStatusPill } from "components/layout/event/EventStatusPill";
import { EventCardLocation } from "components/routes/root/eventCard/EventCardLocation";
import { trackingCategory, trackClick } from "utils/tracking";
import { EventCardSponsors } from "components/routes/root/eventCard/EventCardSponsors";
import { useFeatureFlags } from "components/providers/featureFlags/useFeatureFlags";
import { Sx } from "utils/sx";


export const FeaturedEvents = () => {
    const theme = useTheme();

    const sectionStyle: Sx = (theme) => ({
        marginTop: theme.spacing(6)
    });

    const isNewHomepageHeader = useFeatureFlags("New Homepage Hero");

    const { data } = useQuery<GetFeaturedEventsQuery>(GET_FEATURED_EVENTS);
    const eventPairs = chunk(data?.featuredEvents || [], 2);

    if (!data) return <FeaturedEventsSkeleton />;

    const eventCartdWidth = parseInt(theme.spacing(EVENT_CARD_WIDTH));
    const eventCardPadding = parseInt(theme.spacing(2));
    const eventCardWidthPadding = eventCartdWidth + eventCardPadding;

    return (
        <Section sx={sectionStyle}>
            {isNewHomepageHeader ?
                <Typography variant="h3" component="h3" sx={(theme) => ({ marginBottom: theme.spacing(2), paddingX: theme.spacing(2) })}>
                    <T>Explore live and upcoming events</T>
                </Typography>
                :
                <Stack direction="row" spacing={1} marginBottom={2} paddingX={2}>
                    <Box color="status.livePulse" paddingTop={2}>
                        <LiveIndicator animated={false} size="md" />
                    </Box>
                    <Stack direction="column" color="text.primary">
                        <T>live_now_heading</T>
                    </Stack>
                </Stack>

            }

            <ScrollWithShadows shadowsColor="background.default" showButtons buttonsPositionAbsolute fixedScrollOffset={eventCardWidthPadding}>
                <Stack paddingX={2} spacing={2} direction="row">
                    {eventPairs.map((events, pairIndex) =>
                        <Stack key={pairIndex} direction="column" spacing={2}>
                            {events.map((event, index) => {
                                return <Link
                                    key={index}
                                    to={`/events/${event.id}`}
                                    onClick={trackClick("Event card", trackingCategory.eventsCarousel)}
                                >
                                    <EventCard>
                                        <EventCardMainSection status={event.status}>
                                            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                                                <EventCardTopLabel date={eventCardDate(event, { shortFormat: true })} />
                                                <EventStatusPill status={event.status} />
                                            </Stack>
                                            <EventCardTitle title={event.name} />
                                            <EventCardSponsors sponsoredContents={event.sponsoredContents} />
                                            <EventCardLocation location={event.location} />
                                        </EventCardMainSection>
                                        <EventCardOrganisation organisation={event.organisation} logo={true} status={event.status} sportType={true} />
                                    </EventCard>
                                </Link>;
                            })}
                        </Stack>
                    )}
                </Stack>
            </ScrollWithShadows>
        </Section>
    );
};
